/**
 * @param {Object} rgb
 */
export const getObjectRgbValue = rgb => {
  if (rgb?.r && rgb?.g && rgb?.b) {
    // Opération qui return un nombre, s'il est <= 128 c'est une couleur claire sinon foncé
    const colorValue = 0.3 * rgb?.r + 0.59 * rgb?.g + 0.11 * rgb?.b

    return colorValue
  }

  return -1
}

/**
 * @requires format "rgb(0, 0, 0, 0)""  ||  "0, 0, 0, 0"
 * @param {String} rgb
 */
export const getStringRgbValue = rgb => {
  while (rgb.indexOf(" ") != -1) rgb = rgb.replace(" ", "")

  let x
  // Check si la chaîne de caractères reçue est au format rgb
  if ((x = /([0-9]{0,3}),([0-9]{0,3}),([0-9]{0,3})/.exec(rgb))) {
    const color = {
      r: parseInt(x[1]),
      g: parseInt(x[2]),
      b: parseInt(x[3]),
    }

    // Opération qui return un nombre, s'il est <= 128 c'est une couleur claire sinon foncé
    const colorValue = 0.3 * color["r"] + 0.59 * color["g"] + 0.11 * color["b"]

    return colorValue
  }

  return -1
}

export const hexToRgb = hex => {
  // check nombre de caractère de la couleur hex
  const nbCaractereHex = hex?.length
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegexCaractereHex7 = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const shorthandRegexCaractereHex9 = /^#?([a-f\d])([a-f\d])([a-f\d])([a-f\d])$/i
  const shorthandRegex =
    nbCaractereHex === 9 ? shorthandRegexCaractereHex9 : shorthandRegexCaractereHex7
  hex = hex.replace(shorthandRegex, (m, r, g, b, a) => r + r + g + g + b + b + a + a)

  const resultHex7 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  const resultHex9 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  const result = nbCaractereHex === 9 ? resultHex9 : resultHex7

  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: parseInt(result[4], 16),
      }
    : null
}

export const colorIsDark = color => {
  const isAnHex = color[0] === "#"
  let rgbColor
  let resultRgbCalcul

  if (isAnHex) rgbColor = hexToRgb(color)
  else rgbColor = color

  if (typeof rgbColor === "string") {
    resultRgbCalcul = getStringRgbValue(rgbColor)
  } else if (typeof rgbColor === "object") {
    resultRgbCalcul = getObjectRgbValue(rgbColor)
  } else {
    resultRgbCalcul = -1
    console.error("wrong color format")
  }

  const isDark = resultRgbCalcul <= 128

  return isDark
}

export const darkerOrLighterColor = (color, amount) => {
  return "#" + color
    .replace(/^#/, "")
    .replace(/../g, color =>
      ("0" + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2)
    );
}

/**
 * Compute an accent color from a base hex color using split-complementary hue shift.
 * Shifts hue by +150 degrees, keeps saturation, clamps lightness for readability.
 * @param {string} hex - base color e.g. "#79ad94"
 * @returns {string} accent hex color e.g. "#ad7993"
 */
export const computeAccentColor = (hex) => {
  if (!hex) return "#6366f1"
  const rgb = hexToRgb(hex)
  if (!rgb) return "#6366f1"

  // RGB to HSL
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
    else if (max === g) h = ((b - r) / d + 2) / 6
    else h = ((r - g) / d + 4) / 6
  }

  // Shift hue by 150 degrees (split-complementary)
  h = (h + 150 / 360) % 1
  // Boost saturation slightly, clamp lightness to 35-55% for vibrancy
  const ns = Math.min(1, s * 1.15)
  const nl = Math.max(0.35, Math.min(0.55, l))

  // HSL to RGB
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1
    if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  const q2 = nl < 0.5 ? nl * (1 + ns) : nl + ns - nl * ns
  const p2 = 2 * nl - q2
  const ro = Math.round(hue2rgb(p2, q2, h + 1 / 3) * 255)
  const go = Math.round(hue2rgb(p2, q2, h) * 255)
  const bo = Math.round(hue2rgb(p2, q2, h - 1 / 3) * 255)

  return `#${((1 << 24) + (ro << 16) + (go << 8) + bo).toString(16).slice(1)}`
}

/**
 * Compute menu active/hover colors dynamically.
 * Background = accent, text = primary (cohérent avec titres/icônes de l'app).
 * If primary and accent are too close (insufficient contrast on accent background),
 * darken the primary to guarantee readability.
 *
 * @param {string} accent - accent/complementary color for backgrounds
 * @param {string} primary - primary color for text
 * @returns {{ activeBg: string, hoverBg: string, activeText: string }}
 */
export const computeMenuColors = (accent, primary) => {
  const bg = accent || primary || "#3b82f6"
  const txt = primary || accent || "#3b82f6"

  const bgRgb = hexToRgb(bg)
  const txtRgb = hexToRgb(txt)

  if (!bgRgb) return { activeBg: `${bg}20`, hoverBg: `${bg}14`, activeText: txt }

  // Luminance du fond accent (full-opacity)
  const bgLum = 0.299 * bgRgb.r + 0.587 * bgRgb.g + 0.114 * bgRgb.b

  // Luminance effective du fond actif (opacité ~20% sur blanc)
  const effectiveBgLum = 255 - (255 - bgLum) * 0.2

  const txtLum = txtRgb ? (0.299 * txtRgb.r + 0.587 * txtRgb.g + 0.114 * txtRgb.b) : 128

  // Ratio de contraste simplifié (WCAG-ish)
  const lighter = Math.max(effectiveBgLum, txtLum) + 0.05
  const darker = Math.min(effectiveBgLum, txtLum) + 0.05
  const contrastRatio = lighter / darker

  let activeText = txt
  if (contrastRatio < 1.8) {
    activeText = darkerOrLighterColor(txt, -60)
    const retryRgb = hexToRgb(activeText)
    if (retryRgb) {
      const retryLum = 0.299 * retryRgb.r + 0.587 * retryRgb.g + 0.114 * retryRgb.b
      if (retryLum > 160) activeText = darkerOrLighterColor(txt, -120)
    }
  }

  if (bgLum > 180) {
    return { activeBg: `${bg}38`, hoverBg: `${bg}24`, activeText }
  } else if (bgLum > 120) {
    return { activeBg: `${bg}28`, hoverBg: `${bg}1a`, activeText }
  } else {
    return { activeBg: `${bg}20`, hoverBg: `${bg}14`, activeText }
  }
}
